import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchShopSettings,
  updateShopSettings,
  fetchUsersList,
  updateUserRole,
  updateUserActiveState,
} from '@/services/settings';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ShopSettings, Profile } from '@/types/models';
import { useAuth } from '@/context/AuthContext';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Settings as SettingsIcon,
  Store,
  Users,
  Shield,
  Save,
  Loader2,
  CheckCircle2,
  Info,
} from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'shop' | 'users'>('shop');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Shop Settings Query
  const { data: settings, isLoading: loadingSettings } = useQuery<ShopSettings>({
    queryKey: queryKeys.settings.shop,
    queryFn: () => fetchShopSettings(),
  });

  // Users List Query
  const { data: usersList = [], isLoading: loadingUsers, refetch: refetchUsers } = useQuery<Profile[]>({
    queryKey: queryKeys.settings.users,
    queryFn: () => fetchUsersList(),
  });

  // Shop Form State
  const [shopName, setShopName] = useState('');
  const [slogan, setSlogan] = useState('');
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Pakistan');
  const [phone, setPhone] = useState('');
  const [phoneSecondary, setPhoneSecondary] = useState('');
  const [phoneTertiary, setPhoneTertiary] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('Rs');
  const [defaultWarrantyMonths, setDefaultWarrantyMonths] = useState(1);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [invoiceFooter, setInvoiceFooter] = useState('');

  React.useEffect(() => {
    if (settings) {
      setShopName(settings.shop_name ?? 'AL KUWAIT TECH');
      setSlogan(settings.slogan ?? 'ALWAYS AHEAD.');
      setLegalName(settings.legal_name ?? '');
      setAddress(settings.address ?? 'Shop 264, 2nd Floor Umair Center, 6th Road, Rawalpindi');
      setCity(settings.city ?? 'Rawalpindi');
      setCountry(settings.country ?? 'Pakistan');
      setPhone(settings.phone ?? '042 111 222 345');
      setPhoneSecondary(settings.phone_secondary ?? '+92 370 5478785');
      setPhoneTertiary(settings.phone_tertiary ?? '+92 324 9554169');
      setWhatsapp(settings.whatsapp ?? '');
      setEmail(settings.email ?? '');
      setWebsite(settings.website ?? '');
      setTaxNumber(settings.tax_number ?? '');
      setCurrencySymbol(settings.currency_symbol ?? 'Rs');
      setDefaultWarrantyMonths(settings.default_warranty_months ?? 1);
      setLowStockThreshold(settings.low_stock_threshold ?? 5);
      setInvoiceFooter(settings.invoice_footer ?? 'Thank you for choosing Al Kuwait Tech. We appreciate your business and look forward to serving you again.');
    }
  }, [settings]);

  // Shop Settings Mutation
  const settingsMutation = useMutation({
    mutationFn: async () => {
      return await updateShopSettings({
        shop_name: shopName,
        slogan: slogan || null,
        legal_name: legalName || null,
        address: address || null,
        city: city || null,
        country,
        phone: phone || null,
        phone_secondary: phoneSecondary || null,
        phone_tertiary: phoneTertiary || null,
        whatsapp: whatsapp || null,
        email: email || null,
        website: website || null,
        tax_number: taxNumber || null,
        currency_symbol: currencySymbol,
        default_warranty_months: defaultWarrantyMonths,
        low_stock_threshold: lowStockThreshold,
        invoice_footer: invoiceFooter || null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.shop });
      setSuccessMsg('Shop settings updated successfully!');
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  // User Role Mutation
  const roleMutation = useMutation({
    mutationFn: async ({ profileId, role }: { profileId: string; role: 'owner' | 'manager' | 'staff' }) => {
      return await updateUserRole(profileId, role);
    },
    onSuccess: () => {
      void refetchUsers();
      setSuccessMsg('User role updated!');
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  // User Active Toggle Mutation
  const activeMutation = useMutation({
    mutationFn: async ({ profileId, isActive }: { profileId: string; isActive: boolean }) => {
      if (profileId === user?.id) {
        throw new Error('You cannot deactivate your own profile account.');
      }
      return await updateUserActiveState(profileId, isActive);
    },
    onSuccess: () => {
      void refetchUsers();
      setSuccessMsg('User active status updated!');
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);
    settingsMutation.mutate();
  };

  const inputClass = "w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green";

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <SettingsIcon className="w-5 h-5 text-brand-green" />
            <span>Shop Configuration & Accounts</span>
          </h1>
          <p className="text-brand-muted mt-0.5">Manage shop contact info, terms, user profiles, and role permissions.</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center space-x-2 bg-white p-1 rounded-md border border-brand-border">
          <button
            onClick={() => setActiveTab('shop')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 font-semibold rounded ${
              activeTab === 'shop' ? 'bg-brand-green text-white' : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            <Store className="w-4 h-4" />
            <span>Shop Profile</span>
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 font-semibold rounded ${
              activeTab === 'users' ? 'bg-brand-green text-white' : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>User Accounts & Roles</span>
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 font-semibold flex items-center space-x-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && <ErrorAlert message={errorMsg} />}

      {/* Tab 1: Shop Settings */}
      {activeTab === 'shop' && (
        <form onSubmit={handleSaveSettings} className="bg-white p-4 rounded-xl border border-brand-border space-y-4 shadow-xs">
          <h3 className="font-bold text-sm text-brand-black border-b pb-2 flex items-center space-x-1.5">
            <Store className="w-4 h-4 text-brand-green" />
            <span>Business Information & Invoice Footer</span>
          </h3>

          {loadingSettings ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">
                    Shop Display Name <span className="text-brand-red">*</span>
                  </label>
                  <input value={shopName} onChange={(e) => setShopName(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Brand Slogan / Tagline</label>
                  <input placeholder="e.g. ALWAYS AHEAD." value={slogan} onChange={(e) => setSlogan(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Legal / Registered Name</label>
                  <input value={legalName} onChange={(e) => setLegalName(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Main Phone (Business)</label>
                  <input placeholder="042 111 222 345" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Contact 2 (e.g. Shakeel Hussain)</label>
                  <input placeholder="+92 370 5478785" value={phoneSecondary} onChange={(e) => setPhoneSecondary(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Contact 3 (e.g. Arslan Shakeel)</label>
                  <input placeholder="+92 324 9554169" value={phoneTertiary} onChange={(e) => setPhoneTertiary(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">WhatsApp Number</label>
                  <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Email Address (Optional)</label>
                  <input placeholder="Leave blank if none" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Website URL (Optional)</label>
                  <input placeholder="Leave blank if none" value={website} onChange={(e) => setWebsite(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Address</label>
                  <input value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">City</label>
                  <input value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Country</label>
                  <input value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-brand-bg p-3 rounded-lg border border-brand-border">
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Currency Symbol</label>
                  <input value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Default Warranty (Months)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    value={defaultWarrantyMonths}
                    onChange={(e) => setDefaultWarrantyMonths(Number(e.target.value) || 0)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Low Stock Alert Threshold</label>
                  <input
                    type="number"
                    min={0}
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(Number(e.target.value) || 0)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-brand-black uppercase mb-1">Printable Invoice Footer Text</label>
                <textarea
                  rows={2}
                  value={invoiceFooter}
                  onChange={(e) => setInvoiceFooter(e.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div className="flex justify-end pt-2 border-t border-brand-border">
                <button
                  type="submit"
                  disabled={settingsMutation.isPending}
                  className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover disabled:opacity-50 shadow-sm"
                >
                  {settingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{settingsMutation.isPending ? 'Saving...' : 'Save Settings'}</span>
                </button>
              </div>
            </>
          )}
        </form>
      )}

      {/* Tab 2: User Accounts & Roles */}
      {activeTab === 'users' && (
        <div className="bg-white p-4 rounded-xl border border-brand-border space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b pb-2">
            <div>
              <h3 className="font-bold text-sm text-brand-black flex items-center space-x-1.5">
                <Shield className="w-4 h-4 text-brand-green" />
                <span>Team Accounts & Role Management</span>
              </h3>
              <p className="text-brand-muted">Assign roles (`owner`, `manager`, `staff`) and enable/disable account access.</p>
            </div>

            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-blue-900 text-[11px] flex items-center space-x-1">
              <Info className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>To add a new user, register them in Supabase Auth then assign role below.</span>
            </div>
          </div>

          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-brand-bg border-b text-brand-muted font-semibold uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Full Name</th>
                    <th className="text-left px-4 py-3">Role Permission</th>
                    <th className="text-left px-4 py-3">Active Access</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {usersList.map((usr) => {
                    const isSelf = usr.id === user?.id;
                    return (
                      <tr key={usr.id} className="hover:bg-brand-bg/60 transition-colors">
                        <td className="px-4 py-3 font-semibold text-brand-black">
                          <span>{usr.full_name}</span>
                          {isSelf && <span className="ml-2 px-1.5 py-0.5 bg-brand-green text-white font-mono text-[9px] rounded">YOU</span>}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={usr.role}
                            disabled={roleMutation.isPending}
                            onChange={(e) =>
                              roleMutation.mutate({
                                profileId: usr.id,
                                role: e.target.value as 'owner' | 'manager' | 'staff',
                              })
                            }
                            className="px-2 py-1 bg-white border border-brand-border rounded font-bold uppercase text-[10px]"
                          >
                            <option value="staff">Staff</option>
                            <option value="manager">Manager</option>
                            <option value="owner">Owner</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                              usr.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {usr.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            disabled={isSelf || activeMutation.isPending}
                            onClick={() =>
                              activeMutation.mutate({
                                profileId: usr.id,
                                isActive: !usr.is_active,
                              })
                            }
                            className={`px-3 py-1 text-[11px] font-semibold rounded ${
                              usr.is_active
                                ? 'bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-30'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                          >
                            {usr.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
